import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Box,
  Chip,
  IconButton,
  Button,
  Dialog,
  Grid,
  InputAdornment,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Badge,
  Card,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
  AccountTree as VariationsIcon,
  SwapHoriz as AlternativesIcon,
  Upload as UploadIcon,
  FileDownload as FileDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Download as ImportIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { ExerciseService } from '../../services/exerciseService';
import type { Exercise, Equipment, CsvImportResult } from '../../services/types';
import { getImageUrl } from '../../services/api';
import axios from 'axios';
import { API_BASE_URL as API_URL } from '../../config';
import { useExerciseForm } from '../../hooks/useExerciseForm';
import { useExerciseSearch } from '../../hooks/useExerciseSearch';
import { ExerciseFormDialog } from '../ExerciseFormDialog';
import { ExerciseVariationsPanel } from '../ExerciseVariationsPanel';
import ExerciseAlternativesDialog from './ExerciseAlternativesDialog';
import { htmlToPlainText } from '../../utils/sanitizeHtml';

export const ExerciseManager: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  const [error, setError] = useState<string | null>(null);
  const [equipmentEntities, setEquipmentEntities] = useState<Equipment[]>([]);
  const {
    exercises,
    loading,
    totalCount,
    searchQuery,
    setSearchQuery,
    selectedMuscleGroup,
    setSelectedMuscleGroup,
    selectedCategory,
    setSelectedCategory,
    selectedEquipment,
    setSelectedEquipment,
    selectedDifficulty,
    setSelectedDifficulty,
    muscleGroups,
    categories,
    equipment,
    difficulties,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    clearFilters,
    refresh: searchExercises,
    reloadFilterOptions,
  } = useExerciseSearch({ initialRowsPerPage: 25 });

  // Dialog state
  const [addExerciseDialog, setAddExerciseDialog] = useState(false);
  const [editExerciseDialog, setEditExerciseDialog] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);

  // Image preview
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Variations state
  const [variationsDialog, setVariationsDialog] = useState(false);
  const [variationsExercise, setVariationsExercise] = useState<Exercise | null>(null);
  const [variations, setVariations] = useState<Exercise[]>([]);
  const [loadingVariations, setLoadingVariations] = useState(false);

  // Alternatives (swappable exercises) state
  const [alternativesDialog, setAlternativesDialog] = useState(false);
  const [alternativesExercise, setAlternativesExercise] = useState<Exercise | null>(null);

  // Inline tree expand state: exerciseId → child exercises
  const [expandedExercises, setExpandedExercises] = useState<Set<number>>(new Set());
  const [inlineVariations, setInlineVariations] = useState<Record<number, Exercise[]>>({});
  const [loadingInlineVariations, setLoadingInlineVariations] = useState<Set<number>>(new Set());

  const toggleExpandExercise = useCallback(async (exercise: Exercise) => {
    const id = exercise.id;
    setExpandedExercises((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

    // Load variations on first expand (if not already loaded)
    if (!inlineVariations[id] && !loadingInlineVariations.has(id)) {
      setLoadingInlineVariations((prev) => new Set([...Array.from(prev), id]));
      try {
        const result = await ExerciseService.searchExercises({ parentExerciseId: id, limit: 100 });
        setInlineVariations((prev) => ({ ...prev, [id]: result.exercises }));
      } catch (err) {
        console.error('Failed to load variations:', err);
      } finally {
        setLoadingInlineVariations((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    }
  }, [inlineVariations, loadingInlineVariations]);

  // CSV import state
  const [csvImportDialog, setCsvImportDialog] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState<CsvImportResult | null>(null);

  // Form hook
  const exerciseForm = useExerciseForm();

  const loadEquipmentEntities = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/equipment`, {
        withCredentials: true,
        headers: { 'Accept': 'application/ld+json' },
      });
      const equipmentData = response.data['hydra:member'] || response.data.member || [];
      setEquipmentEntities(equipmentData);
    } catch (err) {
      console.error('Failed to load equipment entities:', err);
    }
  }, []);

  useEffect(() => {
    loadEquipmentEntities();
  }, [loadEquipmentEntities]);

  // Clear inline variation cache when main exercise list refreshes
  useEffect(() => {
    setInlineVariations({});
  }, [exercises]);

  const handleAddExercise = async () => {
    try {
      setError(null);
      await ExerciseService.create(exerciseForm.formData);
      setAddExerciseDialog(false);
      exerciseForm.resetForm();
      await searchExercises();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.description;
      setError(detail || 'Failed to add exercise');
      console.error('Error creating exercise:', err);
    }
  };

  const handleEditExercise = async () => {
    if (!selectedExercise) return;
    try {
      setError(null);
      await ExerciseService.update(selectedExercise.id, exerciseForm.formData);
      setEditExerciseDialog(false);
      setSelectedExercise(null);
      exerciseForm.resetForm();
      await searchExercises();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.description;
      setError(detail || 'Failed to update exercise');
      console.error('Error updating exercise:', err);
    }
  };

  const handleDeleteExercise = async (exercise: Exercise) => {
    if (!window.confirm(`Are you sure you want to delete "${exercise.name}"?`)) return;
    try {
      await ExerciseService.delete(exercise.id);
      await searchExercises();
    } catch (err) {
      setError('Failed to delete exercise');
      console.error(err);
    }
  };

  const handleImportCommonExercises = async () => {
    try {
      const result = await ExerciseService.importCommonExercises();
      setError(null);
      alert(result.message);
      await searchExercises();
    } catch (err) {
      setError('Failed to import exercises');
      console.error(err);
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile) return;
    try {
      setCsvImporting(true);
      setCsvImportResult(null);
      const result = await ExerciseService.importCsv(csvFile);
      setCsvImportResult(result);
      await searchExercises();
      await reloadFilterOptions();
      await loadEquipmentEntities();
    } catch (err: any) {
      setCsvImportResult({
        message: err.message || 'Import failed',
        created: 0,
        updated: 0,
        skipped: 0,
        errors: [err.message || 'Unknown error'],
      });
    } finally {
      setCsvImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await ExerciseService.downloadCsvTemplate();
    } catch (err) {
      setError('Failed to download template');
      console.error(err);
    }
  };

  const handleExportCsv = async () => {
    try {
      await ExerciseService.exportCsv();
    } catch (err) {
      setError('Failed to export exercises');
      console.error(err);
    }
  };

  const openEditDialog = (exercise: Exercise) => {
    setSelectedExercise(exercise);
    exerciseForm.populateFromExercise(exercise);
    setEditExerciseDialog(true);
  };

  const openAlternativesDialog = (exercise: Exercise) => {
    setAlternativesExercise(exercise);
    setAlternativesDialog(true);
  };

  const handleOpenVariations = async (exercise: Exercise) => {
    setVariationsExercise(exercise);
    setVariationsDialog(true);
    setLoadingVariations(true);
    try {
      const data = await ExerciseService.getVariations(exercise.id);
      setVariations(data.variations);
    } catch (err) {
      console.error('Failed to load variations:', err);
    } finally {
      setLoadingVariations(false);
    }
  };

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 3 }, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 3 }}>
        <Typography variant="h5" component="h2">
          Exercise Management
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<UploadIcon />}
            onClick={() => { setCsvFile(null); setCsvImportResult(null); setCsvImportDialog(true); }}
          >
            Import CSV
          </Button>
          <Button
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCsv}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<ImportIcon />}
            onClick={handleImportCommonExercises}
          >
            Import Common
          </Button>
          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<AddIcon />}
            onClick={() => setAddExerciseDialog(true)}
          >
            Add Exercise
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            fullWidth
            label="Search exercises"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Muscle Group</InputLabel>
            <Select
              value={selectedMuscleGroup}
              onChange={(e) => setSelectedMuscleGroup(e.target.value)}
              input={<OutlinedInput label="Muscle Group" />}
            >
              <MenuItem value="">All</MenuItem>
              {muscleGroups.map((group) => (
                <MenuItem key={group} value={group}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: ExerciseService.getMuscleGroupColor(group),
                        mr: 1,
                      }}
                    />
                    {group}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              input={<OutlinedInput label="Category" />}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Equipment</InputLabel>
            <Select
              value={selectedEquipment}
              onChange={(e) => setSelectedEquipment(e.target.value)}
              input={<OutlinedInput label="Equipment" />}
            >
              <MenuItem value="">All</MenuItem>
              {equipment.map((eq) => (
                <MenuItem key={eq} value={eq}>
                  {eq}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Difficulty</InputLabel>
            <Select
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
              input={<OutlinedInput label="Difficulty" />}
            >
              <MenuItem value="">All</MenuItem>
              {difficulties.map((diff) => (
                <MenuItem key={diff} value={diff}>
                  <Chip
                    label={diff}
                    size="small"
                    sx={{
                      backgroundColor: ExerciseService.getDifficultyColor(diff),
                      color: 'white',
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={clearFilters}
            startIcon={<ClearIcon />}
          >
            Clear
          </Button>
        </Grid>
      </Grid>

      {/* Exercise Table (Desktop) */}
      {!isMobile && (
        <TableContainer sx={{ overflowX: 'hidden' }}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48, px: 0.5 }}></TableCell>
                <TableCell>Упражнение</TableCell>
                <TableCell sx={{ width: isCompact ? 65 : 80 }}>Мускул</TableCell>
                {!isCompact && <TableCell sx={{ width: 85 }}>Категория</TableCell>}
                {!isCompact && <TableCell sx={{ width: 90 }}>Уред</TableCell>}
                <TableCell sx={{ width: isCompact ? 55 : 75 }}>Ниво</TableCell>
                {!isCompact && <TableCell sx={{ width: 80 }}>SFR</TableCell>}
                <TableCell sx={{ width: 68, pr: 0.5 }} align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isCompact ? 5 : 8} align="center">
                    Loading exercises...
                  </TableCell>
                </TableRow>
              ) : exercises.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isCompact ? 5 : 8} align="center">
                    No exercises found
                  </TableCell>
                </TableRow>
              ) : (
                exercises.map((exercise) => {
                const isExpanded = expandedExercises.has(exercise.id);
                const childExercises = inlineVariations[exercise.id] || [];
                const isLoadingChildren = loadingInlineVariations.has(exercise.id);
                const hasChildren = exercise.hasVariations && (exercise.variationCount ?? 0) > 0;

                const renderExerciseRow = (ex: Exercise, isChild: boolean) => (
                  <TableRow key={ex.id} hover sx={isChild ? { bgcolor: 'action.hover' } : undefined}>
                    <TableCell sx={{ px: 0.5, py: 0.5 }}>
                      {ex.anatomyImages && ex.anatomyImages.length > 0 ? (
                        <Box
                          component="img"
                          src={getImageUrl(ex.anatomyImages[0])}
                          alt={ex.name}
                          onClick={() => setPreviewImage(getImageUrl(ex.anatomyImages![0]))}
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'scale(1.1)', boxShadow: 2 },
                            ...(isChild ? { ml: 2 } : {}),
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1,
                            backgroundColor: 'action.hover',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#bbb',
                            fontSize: '0.6rem',
                            ...(isChild ? { ml: 2 } : {}),
                          }}
                        >
                          No img
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Tooltip title={htmlToPlainText(ex.description)} placement="bottom-start" arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {!isChild && hasChildren && (
                            <IconButton
                              size="small"
                              onClick={() => toggleExpandExercise(exercise)}
                              sx={{ p: 0, mr: 0.25 }}
                            >
                              {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                            </IconButton>
                          )}
                          {isChild && (
                            <Box sx={{ width: 20, textAlign: 'center', color: 'text.disabled', flexShrink: 0 }}>
                              <Typography variant="body2" sx={{ lineHeight: 1 }}>{'\u2514'}</Typography>
                            </Box>
                          )}
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="subtitle2" fontWeight="bold" noWrap>
                                {isChild ? (ex.variationName || ex.name) : ex.name}
                              </Typography>
                              {isChild && (
                                <Chip label={ex.variationName || 'var'} size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }} />
                              )}
                              {!isChild && hasChildren && (
                                <Badge badgeContent={exercise.variationCount} color="primary" sx={{ ml: 0.5, flexShrink: 0 }}>
                                  <IconButton size="small" onClick={() => handleOpenVariations(exercise)} sx={{ p: 0.25 }}>
                                    <VariationsIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Badge>
                              )}
                              {!isChild && !hasChildren && (
                                <IconButton size="small" onClick={() => handleOpenVariations(exercise)} title="Add variations" sx={{ p: 0.25, flexShrink: 0 }}>
                                  <VariationsIcon sx={{ fontSize: 16 }} color="disabled" />
                                </IconButton>
                              )}
                            </Box>
                            {isCompact && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {[ex.category, ex.equipment].filter(Boolean).join(' · ')}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Tooltip title={ex.muscleGroup} arrow>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: ExerciseService.getMuscleGroupColor(ex.muscleGroup),
                            display: 'inline-block',
                            verticalAlign: 'middle',
                          }}
                        />
                      </Tooltip>
                      <Typography variant="caption" sx={{ ml: 0.5 }} noWrap component="span">
                        {ex.muscleGroup}
                      </Typography>
                    </TableCell>
                    {!isCompact && (
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography variant="caption" noWrap>{ex.category}</Typography>
                      </TableCell>
                    )}
                    {!isCompact && (
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography variant="caption" noWrap>{ex.equipment}</Typography>
                      </TableCell>
                    )}
                    <TableCell sx={{ py: 0.5 }}>
                      <Chip
                        label={ex.difficulty}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          backgroundColor: ExerciseService.getDifficultyColor(ex.difficulty),
                          color: 'white',
                        }}
                      />
                    </TableCell>
                    {!isCompact && (
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography variant="caption" noWrap>{ex.stimulusToFatigueRatio}</Typography>
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ py: 0.5, px: 0.5, whiteSpace: 'nowrap' }}>
                      <IconButton size="small" onClick={() => openEditDialog(ex)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openAlternativesDialog(ex)}
                        title="Алтернативи (заменящи упражнения)"
                        color={ex.alternativeCount ? 'primary' : 'default'}
                      >
                        <AlternativesIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteExercise(ex)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );

                return (
                  <React.Fragment key={exercise.id}>
                    {renderExerciseRow(exercise, false)}
                    {isExpanded && isLoadingChildren && (
                      <TableRow>
                        <TableCell colSpan={isCompact ? 5 : 8} sx={{ py: 1, pl: 6 }}>
                          <Typography variant="caption" color="text.secondary">Зареждане на вариации...</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {isExpanded && childExercises.map((child) => renderExerciseRow(child, true))}
                  </React.Fragment>
                );
              })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Exercise Cards (Mobile) */}
      {isMobile && (
        <Box>
          {loading ? (
            <Typography align="center" sx={{ py: 3 }}>Loading exercises...</Typography>
          ) : exercises.length === 0 ? (
            <Typography align="center" sx={{ py: 3 }} color="text.secondary">No exercises found</Typography>
          ) : (
            exercises.map((exercise) => {
              const isExpanded = expandedExercises.has(exercise.id);
              const childExercises = inlineVariations[exercise.id] || [];
              const isLoadingChildren = loadingInlineVariations.has(exercise.id);
              const hasChildren = exercise.hasVariations && (exercise.variationCount ?? 0) > 0;

              const renderMobileCard = (ex: Exercise, isChild: boolean) => (
                <Card key={ex.id} variant="outlined" sx={{ mb: 1, ...(isChild ? { ml: 3, borderLeft: 3, borderLeftColor: 'primary.main' } : {}) }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1.5 }}>
                    <Box sx={{ flexShrink: 0 }}>
                      {ex.anatomyImages && ex.anatomyImages.length > 0 ? (
                        <Box
                          component="img"
                          src={getImageUrl(ex.anatomyImages[0])}
                          alt={ex.name}
                          onClick={() => setPreviewImage(getImageUrl(ex.anatomyImages![0]))}
                          sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd', cursor: 'pointer' }}
                        />
                      ) : (
                        <Box sx={{ width: 44, height: 44, borderRadius: 1, backgroundColor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: '0.6rem' }}>
                          No img
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {!isChild && hasChildren && (
                          <IconButton size="small" onClick={() => toggleExpandExercise(exercise)} sx={{ p: 0.25 }}>
                            {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                          </IconButton>
                        )}
                        {isChild && <Typography variant="body2" color="text.disabled" sx={{ mr: 0.5 }}>{'\u2514'}</Typography>}
                        <Typography variant="subtitle2" fontWeight="bold" noWrap>
                          {isChild ? (ex.variationName || ex.name) : ex.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        <Chip size="small" label={ex.muscleGroup} sx={{ height: 20, fontSize: '0.7rem', backgroundColor: ExerciseService.getMuscleGroupColor(ex.muscleGroup), color: 'white' }} />
                        <Chip size="small" label={ex.difficulty} sx={{ height: 20, fontSize: '0.7rem', backgroundColor: ExerciseService.getDifficultyColor(ex.difficulty), color: 'white' }} />
                        <Chip size="small" label={`SFR ${ex.stimulusToFatigueRatio}`} sx={{ height: 20, fontSize: '0.7rem' }} />
                        {isChild && <Chip label={ex.variationName || 'var'} size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                        {!isChild && hasChildren && (
                          <Chip label={`${exercise.variationCount} var`} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', cursor: 'pointer' }} onClick={() => toggleExpandExercise(exercise)} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" noWrap>{ex.equipment}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                      <IconButton size="small" onClick={() => openEditDialog(ex)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleDeleteExercise(ex)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openAlternativesDialog(ex)}
                        title="Алтернативи (заменящи упражнения)"
                      >
                        <AlternativesIcon fontSize="small" color={ex.alternativeCount ? 'primary' : 'disabled'} />
                      </IconButton>
                      {!isChild && (
                        <IconButton size="small" onClick={() => handleOpenVariations(exercise)}>
                          <VariationsIcon fontSize="small" color={hasChildren ? 'primary' : 'disabled'} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Card>
              );

              return (
                <React.Fragment key={exercise.id}>
                  {renderMobileCard(exercise, false)}
                  {isExpanded && isLoadingChildren && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 5, mb: 1, display: 'block' }}>Зареждане на вариации...</Typography>
                  )}
                  {isExpanded && childExercises.map((child) => renderMobileCard(child, true))}
                </React.Fragment>
              );
            })
          )}
        </Box>
      )}

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Add Exercise Dialog */}
      <ExerciseFormDialog
        open={addExerciseDialog}
        mode="add"
        onClose={() => setAddExerciseDialog(false)}
        onSave={handleAddExercise}
        formData={exerciseForm.formData}
        onFormChange={exerciseForm.setFormData}
        muscleGroups={muscleGroups}
        categories={categories}
        equipment={equipment}
        equipmentEntities={equipmentEntities}
        newAnatomyImage={exerciseForm.newAnatomyImage}
        onNewAnatomyImageChange={exerciseForm.setNewAnatomyImage}
        newInstructionImage={exerciseForm.newInstructionImage}
        onNewInstructionImageChange={exerciseForm.setNewInstructionImage}
        uploadingAnatomy={exerciseForm.uploadingAnatomy}
        uploadingInstruction={exerciseForm.uploadingInstruction}
        uploadingMistakeImage={exerciseForm.uploadingMistakeImage}
        onAddAnatomyImage={exerciseForm.handleAddAnatomyImage}
        onRemoveAnatomyImage={exerciseForm.handleRemoveAnatomyImage}
        onAnatomyImageUpload={exerciseForm.handleAnatomyImageUpload}
        onAddInstructionImage={exerciseForm.handleAddInstructionImage}
        onRemoveInstructionImage={exerciseForm.handleRemoveInstructionImage}
        onInstructionImageUpload={exerciseForm.handleInstructionImageUpload}
        onAddMistake={exerciseForm.handleAddMistake}
        onRemoveMistake={exerciseForm.handleRemoveMistake}
        onMistakeDescriptionChange={exerciseForm.handleMistakeDescriptionChange}
        onMistakeImageUpload={exerciseForm.handleMistakeImageUpload}
        onRemoveMistakeImage={exerciseForm.handleRemoveMistakeImage}
      />

      {/* Edit Exercise Dialog */}
      <ExerciseFormDialog
        open={editExerciseDialog}
        mode="edit"
        onClose={() => setEditExerciseDialog(false)}
        onSave={handleEditExercise}
        formData={exerciseForm.formData}
        onFormChange={exerciseForm.setFormData}
        muscleGroups={muscleGroups}
        categories={categories}
        equipment={equipment}
        equipmentEntities={equipmentEntities}
        newAnatomyImage={exerciseForm.newAnatomyImage}
        onNewAnatomyImageChange={exerciseForm.setNewAnatomyImage}
        newInstructionImage={exerciseForm.newInstructionImage}
        onNewInstructionImageChange={exerciseForm.setNewInstructionImage}
        uploadingAnatomy={exerciseForm.uploadingAnatomy}
        uploadingInstruction={exerciseForm.uploadingInstruction}
        uploadingMistakeImage={exerciseForm.uploadingMistakeImage}
        onAddAnatomyImage={exerciseForm.handleAddAnatomyImage}
        onRemoveAnatomyImage={exerciseForm.handleRemoveAnatomyImage}
        onAnatomyImageUpload={exerciseForm.handleAnatomyImageUpload}
        onAddInstructionImage={exerciseForm.handleAddInstructionImage}
        onRemoveInstructionImage={exerciseForm.handleRemoveInstructionImage}
        onInstructionImageUpload={exerciseForm.handleInstructionImageUpload}
        onAddMistake={exerciseForm.handleAddMistake}
        onRemoveMistake={exerciseForm.handleRemoveMistake}
        onMistakeDescriptionChange={exerciseForm.handleMistakeDescriptionChange}
        onMistakeImageUpload={exerciseForm.handleMistakeImageUpload}
        onRemoveMistakeImage={exerciseForm.handleRemoveMistakeImage}
      />

      {/* Variations Panel */}
      <ExerciseVariationsPanel
        open={variationsDialog}
        exercise={variationsExercise}
        variations={variations}
        loading={loadingVariations}
        onClose={() => setVariationsDialog(false)}
        onVariationsChange={setVariations}
        onEditVariation={openEditDialog}
        onError={(msg) => setError(msg)}
        onRefreshExercises={searchExercises}
      />

      {/* Alternatives (swappable exercises) Dialog */}
      <ExerciseAlternativesDialog
        open={alternativesDialog}
        exercise={alternativesExercise}
        onClose={() => setAlternativesDialog(false)}
        onChanged={searchExercises}
      />

      {/* Image Preview Modal */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        fullScreen={isMobile}
        PaperProps={{
          sx: isMobile
            ? { backgroundColor: '#000', display: 'flex', justifyContent: 'center', alignItems: 'center' }
            : { backgroundColor: 'transparent', boxShadow: 'none' },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              },
            }}
          >
            <ClearIcon />
          </IconButton>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="Preview"
              sx={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: isMobile ? 0 : 2,
              }}
            />
          )}
        </Box>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvImportDialog} onClose={() => setCsvImportDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <Typography variant="h6" sx={{ p: 2, pb: 0 }}>Import Exercises from CSV</Typography>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              Upload a CSV file to import or update exercises. Existing exercises (matched by name) will be updated.
              New exercises will be created.
            </Alert>

            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{ alignSelf: 'flex-start' }}
            >
              Download CSV Template
            </Button>

            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              {csvFile ? csvFile.name : 'Choose CSV File'}
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={(e) => {
                  setCsvFile(e.target.files?.[0] || null);
                  setCsvImportResult(null);
                }}
              />
            </Button>

            {csvImportResult && (
              <Box>
                <Alert severity={csvImportResult.errors.length > 0 ? 'warning' : 'success'} sx={{ mb: 1 }}>
                  {csvImportResult.message}
                </Alert>
                {csvImportResult.errors.length > 0 && (
                  <Alert severity="error" sx={{ maxHeight: 200, overflow: 'auto' }}>
                    <Typography variant="subtitle2" gutterBottom>Errors:</Typography>
                    {csvImportResult.errors.map((err, i) => (
                      <Typography key={i} variant="body2">{err}</Typography>
                    ))}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, pt: 0 }}>
          <Button onClick={() => setCsvImportDialog(false)}>Close</Button>
          <Button
            onClick={handleCsvImport}
            variant="contained"
            disabled={!csvFile || csvImporting}
            startIcon={<UploadIcon />}
          >
            {csvImporting ? 'Importing...' : 'Import'}
          </Button>
        </Box>
      </Dialog>
    </Paper>
  );
};

export default ExerciseManager;
